/*
 * Copyright (c) 2022. Nedaware SL
 */

import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse} from '@angular/common/http';
import {ApiInterface} from './api-interface';
import {Token} from './session/token';
import {concat, Observable, of, OperatorFunction, throwError} from 'rxjs';
import {CollectionAnswerInterface, Paginated} from './collection-answer';
import {catchError, map, switchMap} from 'rxjs/operators';
import {ErrorDisplayService} from "../basic-entity-front/services/error-display.service";

export type PaginatedResponse<T> = HttpResponse<Paginated<T>>;
export type ObservablePagResp<T> = Observable<PaginatedResponse<T>>;

export type ApiResponseType = 'arraybuffer' | 'blob' | 'text' | 'json';

@Injectable({
    providedIn: "root"
})
export class ApiService {
    private readonly _options;

    constructor(private http: HttpClient, private _errorDiplay: ErrorDisplayService) {
        this._options = {
            observe: 'response', // Esto hace que recibamos HttpResponse
            headers: new HttpHeaders().set('Content-Type', 'application/ld+json')
                .set('Accept', 'application/ld+json, application/json, text/plain, */*')
        };
        this._updateAuthorization();
    }

    public static sCompleteUrl(url: string): string {
        return new URL(url, ApiInterface.URL).toString();
    }

    public static sSetOrderParams(params: HttpParams, orderParam?: string, orderDirection?: 'asc' | 'desc'): HttpParams {
        if (orderParam && orderDirection) {
            const key = 'order[' + orderParam + ']';
            if (params.has(key)) {
                params = params.delete(key);
            }
            params = params.set(key, orderDirection);
        }
        return params;
    }

    /** Takes only the body of the responses */
    public static sTakeBody<T>(): OperatorFunction<HttpResponse<T>, T> {
        return map((res: HttpResponse<T>) => res.body);
    }

    /** Converts an array to a paginated */
    public static sPaginate<T>(): OperatorFunction<Array<T>, Paginated<T>> {
        return map((arr: Array<T>) => Paginated.fromArray(arr));
    }

    /** Converts the paginated in the answer from a type to another using the provided function */
    public static sPaginationConversion<T, V>(func: (x: T) => V): OperatorFunction<Paginated<T>, Paginated<V>> {
        return map((x: Paginated<T>) => x.clone(func));
    }


    public get<T>(url: string, params?: HttpParams, extraHeaders?: HttpHeaders,
                  responseType?: ApiResponseType): Observable<HttpResponse<T>> {
        this._updateAuthorization();
        return this.http.get<T>(
            ApiService.sCompleteUrl(url),
            this._ellaborateOptions(params, extraHeaders, responseType)
        ).pipe(
            catchError(this._catchError.bind(this))
        ) as Observable<HttpResponse<T>>;
    }

    /** Metodo conveniente cuando sabemos que la respuesta será una colección de datos */
    public getCollection<T>(url: string, params: HttpParams = new HttpParams(), pageIndex?: number, pageSize?: number): ObservablePagResp<T> {
        if (pageIndex !== null && pageIndex >= 0) {
            if (params.has('pageIndex')) {
                params = params.delete('pageIndex');
            }
            params = params.append('page', (pageIndex + 1).toString());
        }
        if (pageSize !== null && pageSize >= 1) {
            params = params.append('itemsPerPage', pageSize.toString());
        }
        return this.get<CollectionAnswerInterface<T>>(url, params)
            .pipe(map((x: HttpResponse<any>) => {
                if (Paginated.isNotPaginatedAnswerInterface(x.body)) {
                    throw new Error('The received body is not paginated!');
                }
                return x.clone<Paginated<T>>({body: new Paginated<T>(x.body)});
            }));
    }

    /** Método para obtener todas las páginas, una tras otra, para una consulta */
    public getCollectionAndFollow<T>(url: string, params?: HttpParams, pageIndex?: number, pageSize?: number,
                                     pageLimit: number = 50): ObservablePagResp<T> {
        return this.getCollection<T>(url, params, pageIndex, pageSize)
            .pipe(
                switchMap(page => {
                    const observables: ObservablePagResp<T>[] = [of(page)];
                    if (page) {
                        const pg = page.body;
                        if (!pg.canBeInOrOverLastPage()) {
                            for (let extra = 0;
                                 extra < pg.lastPage - pg.currentPage && (pageLimit < 0 || extra <= pageLimit);
                                 extra++) {
                                observables.push(this.getCollection(url, params, pg.currentPage + extra, pageSize));
                            }
                        }
                    }
                    return concat(...observables);
                })
            );
    }

    public post<T>(url: string, body: any, params?: HttpParams, extraHeaders?: HttpHeaders,
                   responseType?: ApiResponseType): Observable<HttpResponse<T>> {
        this._updateAuthorization();
        return this.http.post<T>(
            ApiService.sCompleteUrl(url),
            body,
            this._ellaborateOptions(params, extraHeaders, responseType),
        ).pipe(
            catchError(this._catchError.bind(this))
        ) as Observable<HttpResponse<T>>;
    }

    public filePost<T>(url: string, name: string, file: File, params?: HttpParams, extraHeaders?: HttpHeaders,
                       responseType?: ApiResponseType): Observable<HttpResponse<T>> {
        this._updateAuthorization();
        const fd = new FormData();
        fd.append(name, file);
        const newOpts = this._ellaborateOptions(params, extraHeaders, responseType);
        if (newOpts.headers) {
            newOpts.headers = (newOpts.headers as HttpHeaders).delete('Content-Type');
        }
        return this.http.post<T>(
            new URL(url, ApiInterface.URL).toString(), fd, newOpts) as Observable<HttpResponse<T>>;
    }

    public put<T>(url: string, body: any, params?: HttpParams, extraHeaders?: HttpHeaders): Observable<HttpResponse<T>> {
        this._updateAuthorization();
        return this.http.put<T>(
            ApiService.sCompleteUrl(url),
            body,
            this._ellaborateOptions(params, extraHeaders)
        ).pipe(
            catchError(this._catchError.bind(this))
        ) as Observable<HttpResponse<T>>;
    }

    public patch<T>(url: string, body: any, params?: HttpParams, extraHeaders?: HttpHeaders): Observable<HttpResponse<T>> {
        this._updateAuthorization();
        return this.http.patch<T>(
            ApiService.sCompleteUrl(url),
            body,
            this._ellaborateOptions(params, extraHeaders)
        ).pipe(
            catchError(this._catchError.bind(this))
        ) as Observable<HttpResponse<T>>;
    }

    public del<T>(url: string, params?: HttpParams, extraHeaders?: HttpHeaders): Observable<HttpResponse<T>> {
        this._updateAuthorization();

        return this.http.delete<T>(
            ApiService.sCompleteUrl(url),
            this._ellaborateOptions(params, extraHeaders),
        ).pipe(
            catchError(this._catchError.bind(this))
        ) as Observable<HttpResponse<T>>;

    }

    /**
     * To catch the errors in the requests
     */
    private _catchError(err) {
        if (err instanceof HttpErrorResponse) {
            this._errorDiplay.displayError(err)
            return throwError(err);
        } else {
            throw err;
        }
    }

    /**
     * Reloads the token for authorization from the local storage
     */
    private _updateAuthorization() {
        const token = Token.fromLocalStorage();
        if (token.isValid()) {
            this._options.headers = this._options.headers.set(
                'Authorization',
                `Bearer ${token.token}`);
        } else {
            this._options.headers = this._options.headers.delete('Authorization');

        }
    }

    private _ellaborateOptions(params?: HttpParams, mergeHeaders?: HttpHeaders, responseType?: 'arraybuffer' | 'blob' | 'text' | 'json') {
        const options = {...this._options, params: params};
        if (mergeHeaders) {
            if (options.headers) {
                let headers = options.headers;
                if (headers instanceof HttpHeaders) {
                    for (const k of mergeHeaders.keys()) {
                        headers = headers.set(k, mergeHeaders.getAll(k));
                    }
                    options.headers = headers;
                } else {
                    throw new Error('Could not mix extra headers because options.headers is not an HttpHeaders instance.');
                }
            } else {
                options.headers = mergeHeaders;
            }
        }
        if (responseType) {
            options.responseType = responseType;
        }
        return options;
    }
}
